import { AnimatePresence, motion } from "framer-motion"
import { Clock, Search, X } from "lucide-react"
import { type ReactNode, type RefObject, useEffect, useRef, useState } from "react"
import { useDebounced } from "@/shared/hooks/use-debounced"
import { fadeIn } from "@/shared/lib/motion"
import { Button } from "@/shared/ui/button"
import { Input } from "@/shared/ui/input"

interface PostsSearchBarProps {
	value: string
	onChange: (value: string) => void
	onSearch: () => void
	inputRef?: RefObject<HTMLInputElement | null>
	trailing?: ReactNode
	autoSearch?: boolean
	recentQueries?: string[]
	onPickRecent?: (value: string) => void
}

export function PostsSearchBar({
	value,
	onChange,
	onSearch,
	inputRef,
	trailing,
	autoSearch = false,
	recentQueries,
	onPickRecent,
}: PostsSearchBarProps) {
	const [focused, setFocused] = useState(false)
	const debouncedValue = useDebounced(value, 300)
	const isFirstRun = useRef(true)
	const onSearchRef = useRef(onSearch)
	onSearchRef.current = onSearch

	// biome-ignore lint/correctness/useExhaustiveDependencies: debouncedValue — триггер повтора, а не данные эффекта; onSearch берётся из рефа
	useEffect(() => {
		if (!autoSearch) return
		if (isFirstRun.current) {
			isFirstRun.current = false
			return
		}
		onSearchRef.current()
	}, [debouncedValue, autoSearch])

	const showRecent = autoSearch && focused && !value && !!recentQueries?.length

	return (
		<div className={showRecent ? "relative z-[51] flex gap-2" : "flex gap-2"}>
			<div className="relative min-w-0 flex-1">
				<Search className="pointer-events-none absolute top-1/2 left-3.5 z-30 size-4 -translate-y-1/2 text-muted-foreground" />
				<Input
					ref={inputRef}
					type="search"
					placeholder="Поиск материалов"
					aria-label="Поиск материалов"
					enterKeyHint="search"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onFocus={() => setFocused(true)}
					onBlur={() => setFocused(false)}
					onKeyDown={(e) => {
						if (e.key !== "Enter") return
						if (!autoSearch) onSearch()
						e.currentTarget.blur()
					}}
					className="relative z-20 h-12 rounded-2xl border-transparent bg-card pr-12 pl-10 ring-1 ring-white/10 focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-primary/40"
				/>
				{value && (
					<Button
						type="button"
						variant="ghost"
						size="icon"
						aria-label="Очистить поиск"
						onClick={() => onChange("")}
						className="absolute top-0 right-0 z-30 h-full w-12 rounded-none hover:bg-transparent"
					>
						<X className="size-4" />
					</Button>
				)}

				<AnimatePresence>
					{showRecent && (
						<motion.div
							aria-hidden
							className="fixed inset-0 z-10 bg-background/70 backdrop-blur-sm"
							variants={fadeIn}
							initial="hidden"
							animate="visible"
							exit="hidden"
						/>
					)}
				</AnimatePresence>

				{showRecent && (
					<div className="absolute top-full right-0 left-0 z-30 mt-2 overflow-hidden rounded-2xl bg-card shadow-2xl shadow-black/50 ring-1 ring-white/10">
						{recentQueries?.map((query) => (
							<button
								key={query}
								type="button"
								onMouseDown={(e) => {
									e.preventDefault()
									onPickRecent?.(query)
								}}
								className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm transition-colors active:bg-white/5"
							>
								<Clock className="size-4 shrink-0 text-muted-foreground" />
								<span className="min-w-0 truncate">{query}</span>
							</button>
						))}
					</div>
				)}
			</div>
			{!autoSearch && (
				<Button type="button" onClick={onSearch} className="h-12 shrink-0 rounded-2xl">
					Найти
				</Button>
			)}
			{trailing ? <div className="relative z-20 flex shrink-0">{trailing}</div> : null}
		</div>
	)
}
